import { useState, useCallback, useEffect, useRef } from "react";
import * as api from "./api";

// ============================================================================
// DEMO DATA — Realistic scenario across 3 clients
// ============================================================================
const INITIAL_CLIENTS = [
  { id: "c1", company_name: "Meridian Labs", name: "Sarah Chen", contact_email: "sarah@meridianlabs.com", industry: "Biotech", headcount: 85, is_active: true },
  { id: "c2", company_name: "Vantage Financial", name: "Marcus Webb", contact_email: "marcus@vantagefin.com", industry: "Fintech", headcount: 140, is_active: true },
  { id: "c3", company_name: "Canopy Health", name: "Dr. Priya Patel", contact_email: "priya@canopyhealth.org", industry: "Healthcare", headcount: 52, is_active: true },
];

const INITIAL_ROLES = [
  { id: "r1", client_id: "c1", title: "Senior Product Manager", department: "Product", location: "Remote", status: "open", calendly_label: "Senior PM", job_description: "Lead product strategy for Meridian's clinical data platform. Own the roadmap, drive cross-functional execution, and translate complex biotech requirements into user-centric features.", key_requirements: "8+ years product management, healthcare/biotech experience, API platform experience, cross-functional leadership" },
  { id: "r2", client_id: "c1", title: "DevOps Engineer", department: "Engineering", location: "San Francisco, CA", status: "open", calendly_label: "DevOps Engineer" },
  { id: "r3", client_id: "c1", title: "UX Researcher", department: "Design", location: "Remote", status: "open", calendly_label: "UX Researcher" },
  { id: "r4", client_id: "c2", title: "VP of Engineering", department: "Engineering", location: "New York, NY", status: "open", calendly_label: "VP Engineering" },
  { id: "r5", client_id: "c2", title: "Data Analyst", department: "Analytics", location: "Remote", status: "open", calendly_label: "Data Analyst" },
  { id: "r6", client_id: "c3", title: "Clinical Operations Lead", department: "Operations", location: "Boston, MA", status: "open", calendly_label: "Clinical Ops Lead" },
];

const INITIAL_CANDIDATES = [
  { id: "ca1", role_id: "r1", first_name: "Aiden", last_name: "Nakamura", email: "aiden.n@gmail.com", phone: "(415) 555-0142", stage: "client_interview", source: "calendly", days_in_stage: 2, ai_recommendation: "strong_yes", scheduled_at: "2026-05-05T14:00:00Z", linkedin_url: "https://linkedin.com/in/aiden-nakamura", trust_score: 88, integrity_signals: JSON.stringify([
    { scorer: "linkedin_presence", score: 1.0, signal: "LinkedIn profile provided", available: true },
    { scorer: "mx_record", score: 1.0, signal: "Domain has a valid mail server", available: true },
    { scorer: "domain_age", score: 1.0, signal: "Domain registered over 1 year ago", available: true },
  ]), prep_brief: "**Quick Context**\nAiden Nakamura is a senior product manager with 8 years of experience, most recently leading a 12-person team at Stripe focused on API platform products. He has direct biotech exposure through a previous role at Tempus AI. Strong fit for Meridian's clinical data platform role.\n\n**3 Tailored Screening Questions**\n1. Walk me through how you'd prioritize features for Meridian's clinical data platform, balancing research team needs vs. regulatory requirements\n2. Describe a time you managed competing stakeholder priorities across engineering and clinical/scientific teams\n3. How do you approach user research in regulated industries where direct patient data access is restricted?\n\n**Key Qualifications to Probe**\nCross-functional leadership at scale, regulated industry product work, API platform architecture understanding, comfort with clinical/scientific stakeholders who think differently than engineers\n\n**Flags or Notes**\nNone — strong fit on paper. Verify depth of biotech domain knowledge vs. breadth. His Tempus role may have been more data/ML focused than clinical operations." },
  { id: "ca2", role_id: "r1", first_name: "Maya", last_name: "Rodriguez", email: "maya.r@outlook.com", stage: "client_screening", source: "calendly", days_in_stage: 1, ai_recommendation: "yes", scheduled_at: "2026-05-04T10:00:00Z" },
  { id: "ca3", role_id: "r1", first_name: "Jordan", last_name: "Walsh", email: "j.walsh@protonmail.com", stage: "initial_screening", source: "calendly", days_in_stage: 0, ai_recommendation: null, scheduled_at: "2026-05-06T15:30:00Z", prep_brief: "**Quick Context**\nJordan Walsh scheduled via Calendly for the Senior PM role. Limited information available — no LinkedIn provided. Form responses indicate 6 years PM experience, currently at a Series B healthtech startup.\n\n**3 Tailored Screening Questions**\n1. Tell me about the healthtech product you're currently managing — what's the regulatory landscape?\n2. Meridian's platform serves both researchers and clinicians. How have you handled building for dual user personas?\n3. What drew you to this specific role at Meridian?\n\n**Key Qualifications to Probe**\nHealthcare domain depth, platform vs. feature-level product thinking, stakeholder management with scientific teams\n\n**Flags or Notes**\nNo LinkedIn URL provided — worth confirming professional background. Healthtech startup experience is relevant but verify scale (team size, ARR, user base).\n\nVERIFICATION NOTES (trust score 28/100 — not automated, for your judgment):\nDomain registered 9 days ago; No LinkedIn profile provided", trust_score: 28, integrity_dismissed: false, integrity_signals: JSON.stringify([
    { scorer: "domain_age", score: 0.2, signal: "Domain registered 9 days ago", available: true },
    { scorer: "linkedin_presence", score: 0.5, signal: "No LinkedIn profile provided", available: true },
    { scorer: "mx_record", score: 1.0, signal: "Domain has a valid mail server", available: true },
    { scorer: "email_name_consistency", score: 1.0, signal: "Email local-part is consistent with candidate name", available: true },
    { scorer: "no_show_history", score: 1.0, signal: "No prior no-show history", available: true },
  ]) },
  { id: "ca4", role_id: "r1", first_name: "Preet", last_name: "Kaur", email: "preet.kaur@gmail.com", stage: "initial_screening", source: "manual", days_in_stage: 3, ai_recommendation: null },
  { id: "ca5", role_id: "r1", first_name: "David", last_name: "Okonkwo", email: "dokonkwo@yahoo.com", stage: "submitted", source: "calendly", days_in_stage: 4, ai_recommendation: "maybe", scheduled_at: "2026-04-29T11:00:00Z" },
  { id: "ca6", role_id: "r2", first_name: "Lisa", last_name: "Fernandez", email: "lisa.f@gmail.com", stage: "client_screening", source: "calendly", days_in_stage: 2, ai_recommendation: "yes", scheduled_at: "2026-05-01T13:00:00Z" },
  { id: "ca7", role_id: "r2", first_name: "Chris", last_name: "Yamamoto", email: "cyamamoto@pm.me", stage: "initial_screening", source: "calendly", days_in_stage: 1, ai_recommendation: null, scheduled_at: "2026-05-05T09:00:00Z" },
  { id: "ca8", role_id: "r2", first_name: "Sam", last_name: "Patel", email: "sam.p@gmail.com", stage: "offer", source: "referral", days_in_stage: 1, ai_recommendation: "strong_yes" },
  { id: "ca9", role_id: "r4", first_name: "Nadia", last_name: "Kowalski", email: "nadia.k@outlook.com", stage: "client_interview", source: "calendly", days_in_stage: 3, ai_recommendation: "yes", scheduled_at: "2026-04-30T16:00:00Z" },
  { id: "ca10", role_id: "r4", first_name: "Ravi", last_name: "Shankar", email: "ravi.s@gmail.com", stage: "client_screening", source: "calendly", days_in_stage: 5, ai_recommendation: "maybe", scheduled_at: "2026-04-28T14:00:00Z" },
  { id: "ca11", role_id: "r4", first_name: "Elena", last_name: "Vasquez", email: "elena.v@protonmail.com", stage: "initial_screening", source: "calendly", days_in_stage: 0, ai_recommendation: null, scheduled_at: "2026-05-06T11:00:00Z" },
  { id: "ca12", role_id: "r6", first_name: "James", last_name: "O'Brien", email: "james.ob@gmail.com", stage: "submitted", source: "calendly", days_in_stage: 2, ai_recommendation: "yes", scheduled_at: "2026-04-30T10:00:00Z" },
  { id: "ca13", role_id: "r6", first_name: "Ana", last_name: "Santos", email: "ana.santos@gmail.com", stage: "initial_screening", source: "calendly", days_in_stage: 1, ai_recommendation: null, scheduled_at: "2026-05-05T14:30:00Z" },
  { id: "ca15", role_id: "r3", first_name: "Hana", last_name: "Lindqvist", email: "hana.l@example.com", stage: "benched", source: "calendly", days_in_stage: 12, ai_recommendation: null, bench_reason: "Strong SQL and dashboarding background, cohort analysis in Looker — wrong fit for UX research." },
  { id: "ca14", role_id: "r5", first_name: "Tyler", last_name: "Kim", email: "tyler.k@gmail.com", stage: "client_screening", source: "calendly", days_in_stage: 2, ai_recommendation: "yes", scheduled_at: "2026-05-01T09:00:00Z" },
];

const INITIAL_ACTIVITY = [
  { id: "a1", action: "prep_brief_generated", detail: "Prep brief generated for Jordan Walsh — Senior PM at Meridian Labs", actor: "system", created_at: "2026-05-03T09:14:00Z" },
  { id: "a2", action: "candidate_created", detail: "Jordan Walsh scheduled via Calendly for Senior Product Manager", actor: "calendly_webhook", created_at: "2026-05-03T09:12:00Z" },
  { id: "a3", action: "stage_changed", detail: "Aiden Nakamura: submitted → client_interview", actor: "you@email.com", created_at: "2026-05-03T08:45:00Z" },
  { id: "a4", action: "stage_changed", detail: "Sam Patel: client_interview → offer", actor: "you@email.com", created_at: "2026-05-02T16:20:00Z" },
  { id: "a5", action: "email_sent", detail: "Pipeline report delivered to Meridian Labs", actor: "system", created_at: "2026-05-02T08:00:00Z" },
  { id: "a6", action: "stage_changed", detail: "Nadia Kowalski: client_screening → client_interview", actor: "you@email.com", created_at: "2026-05-01T14:30:00Z" },
  { id: "a7", action: "candidate_created", detail: "Elena Vasquez scheduled via Calendly for VP of Engineering", actor: "calendly_webhook", created_at: "2026-05-01T11:05:00Z" },
  { id: "a8", action: "prep_brief_generated", detail: "Prep brief generated for Elena Vasquez — VP Engineering at Vantage Financial", actor: "system", created_at: "2026-05-01T11:06:00Z" },
  { id: "a9", action: "stage_changed", detail: "Lisa Fernandez: initial_screening → client_screening", actor: "you@email.com", created_at: "2026-04-30T15:00:00Z" },
  { id: "a10", action: "candidate_created", detail: "Ana Santos scheduled via Calendly for Clinical Operations Lead", actor: "calendly_webhook", created_at: "2026-04-30T10:22:00Z" },
];

// ============================================================================
// CONSTANTS
// ============================================================================
const STAGES = ["initial_screening", "submitted", "client_screening", "client_interview", "client_interview_2", "client_interview_3", "client_final_round", "offer"];
// Pipeline board columns: the active stages plus the two terminal outcomes the client wants visible on the board.
const PIPELINE_BOARD_STAGES = [...STAGES, "start", "withdrawn"];
// Order candidates can be moved through, per client-requested terminology.
const MOVE_STAGES = ["initial_screening", "submitted", "client_screening", "client_interview", "client_interview_2", "client_interview_3", "client_final_round", "offer", "start", "benched", "withdrawn"];
const STAGE_LABELS = { new: "New", initial_screening: "Initial Screening", submitted: "Submitted", client_screening: "Client Screening", client_interview: "Client Interview", client_interview_2: "Client Interview 2", client_interview_3: "Client Interview 3", client_final_round: "Client Final Round", offer: "Offer", start: "Start", benched: "Benched", rejected: "Rejected", withdrawn: "Withdrawn" };
const STAGE_COLORS = { new: "#a8a29e", initial_screening: "#6366f1", submitted: "#f59e0b", client_screening: "#3b82f6", client_interview: "#10b981", client_interview_2: "#10b981", client_interview_3: "#10b981", client_final_round: "#0d9488", offer: "#8b5cf6", start: "#16a34a", benched: "#7c3aed", rejected: "#dc2626", withdrawn: "#a8a29e" };
const STAGE_BGS = { new: "#f5f5f4", initial_screening: "#e0e7ff", submitted: "#fef3c7", client_screening: "#dbeafe", client_interview: "#d1fae5", client_interview_2: "#d1fae5", client_interview_3: "#d1fae5", client_final_round: "#ccfbf1", offer: "#ede9fe", start: "#f0fdf4", benched: "#f5f3ff", rejected: "#fef2f2", withdrawn: "#f5f5f4" };

const S = {
  font: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  bg: "#fafaf9", surface: "#ffffff", border: "#e7e5e4", borderLight: "#f5f5f4",
  text: "#1c1917", textSec: "#78716c", textTert: "#a8a29e",
  accent: "#1c1917", accentSoft: "#f5f5f4",
  blue: "#2563eb", blueSoft: "#eff6ff", green: "#16a34a", greenSoft: "#f0fdf4",
  amber: "#d97706", amberSoft: "#fffbeb", red: "#dc2626", redSoft: "#fef2f2",
  purple: "#7c3aed", purpleSoft: "#f5f3ff",
  radius: 10, radiusSm: 7,
  shadow: "0 1px 3px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 12px rgba(0,0,0,0.07)",
};

const recColors = { strong_yes: S.green, yes: S.blue, maybe: S.amber, no: S.red, strong_no: S.red };
const recLabels = { strong_yes: "Strong Yes", yes: "Yes", maybe: "Maybe", no: "No", strong_no: "Strong No" };
const recBgs = { strong_yes: S.greenSoft, yes: S.blueSoft, maybe: S.amberSoft, no: S.redSoft, strong_no: S.redSoft };

const Icons = {
  home: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  pipeline: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  people: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  briefcase: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
  building: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg>,
  back: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  plus: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  dot: <svg width="7" height="7" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="currentColor"/></svg>,
  mail: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>,
  clock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  link: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>,
  phone: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>,
  chart: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="5" width="3" height="13"/></svg>,
  linkedin: <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 110-4.14 2.07 2.07 0 010 4.14zM7.12 20.45H3.56V9h3.56v11.45z"/></svg>,
};

function timeAgo(d) { const m = Math.floor((new Date() - new Date(d)) / 60000); if (m < 60) return `${m}m ago`; const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`; }
// Mirrors backend/app/utils/linkedin.py normalize_linkedin_url: only /in/
// profile URLs, canonicalized to https://www.linkedin.com/in/<slug>.
const LINKEDIN_PROFILE_RE = /^(?:https?:\/\/)?(?:[a-z0-9-]{1,10}\.)?linkedin\.com\/in\/([A-Za-z0-9\-_%]{3,100})(?:[/?#].*)?$/i;
function normalizeLinkedIn(value) {
  const m = (value || "").trim().match(LINKEDIN_PROFILE_RE);
  return m ? `https://www.linkedin.com/in/${m[1]}` : null;
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""; }

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
function Badge({ label, color, bg }) {
  return <span style={{ display: "inline-flex", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, color: color || S.textSec, background: bg || S.accentSoft, letterSpacing: "0.02em", whiteSpace: "nowrap" }}>{label}</span>;
}

// Trust is the default state, flags are the exception — a high score renders
// as a small chip like any other badge, never oversized or alarm-colored;
// only <70 gets a warmer color, and the signal list is opt-in via click.
function TrustBadge({ score, signals }) {
  const [expanded, setExpanded] = useState(false);
  if (score == null) return null;

  const color = score >= 70 ? S.green : score >= 40 ? S.amber : S.red;
  const bg = score >= 70 ? S.greenSoft : score >= 40 ? S.amberSoft : S.redSoft;

  let parsed = [];
  try { parsed = signals ? JSON.parse(signals) : []; } catch { parsed = []; }

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setExpanded(e => !e)} style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }} title="Click to view trust signals">
        <Badge label={`Trust ${score}`} color={color} bg={bg} />
      </button>
      {expanded && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 10, width: 290, background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radiusSm, boxShadow: S.shadowMd, padding: "12px 14px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Trust Signals</div>
          {parsed.length === 0 && <div style={{ fontSize: 12.5, color: S.textTert }}>No signals recorded yet.</div>}
          {parsed.map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: i < parsed.length - 1 ? `1px solid ${S.borderLight}` : "none" }}>
              <span style={{ fontSize: 12, color: s.available ? S.text : S.textTert }}>{s.signal}</span>
              <span style={{ fontSize: 11, color: S.textTert, flexShrink: 0 }}>{s.available ? Math.round(s.score * 100) : "n/a"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, variant = "default", style: sx, ...rest }) {
  const styles = {
    default: { background: S.surface, border: `1px solid ${S.border}`, color: S.text },
    primary: { background: S.text, border: `1px solid ${S.text}`, color: "#fff" },
    danger: { background: S.redSoft, border: "1px solid #fecaca", color: S.red },
    ghost: { background: "transparent", border: "1px solid transparent", color: S.textSec },
  };
  return <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: S.radiusSm, fontSize: 13, fontWeight: 550, fontFamily: S.font, cursor: "pointer", transition: "all 0.12s", ...styles[variant], ...sx }} {...rest}>{children}</button>;
}

function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: S.textSec, marginBottom: 5, letterSpacing: "0.02em" }}>{label}</label>}
      <input style={{ width: "100%", padding: "9px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, fontSize: 13.5, fontFamily: S.font, color: S.text, outline: "none", background: S.surface, transition: "border 0.15s" }} {...props} />
    </div>
  );
}

// A Calendly booking whose role answer matched no role arrives with role_id
// null; every place that shows a role renders this instead of a blank.
const NEEDS_ROLE = "Needs role";
function RoleTitle({ role }) {
  return role ? <>{role.title}</> : <span style={{ color: S.amber, fontWeight: 600 }}>{NEEDS_ROLE}</span>;
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: S.textSec, marginBottom: 5 }}>{label}</label>}
      <select style={{ width: "100%", padding: "9px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, fontSize: 13.5, fontFamily: S.font, color: S.text, outline: "none", background: S.surface, cursor: "pointer" }} {...props}>{children}</select>
    </div>
  );
}

function Textarea({ label, rows = 3, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: S.textSec, marginBottom: 5, letterSpacing: "0.02em" }}>{label}</label>}
      <textarea rows={rows} style={{ width: "100%", padding: "9px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, fontSize: 13.5, fontFamily: S.font, color: S.text, outline: "none", background: S.surface, resize: "vertical" }} {...props} />
    </div>
  );
}

function Modal({ title, onClose, children, width = 460 }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(2px)" }} />
      <div onClick={e => e.stopPropagation()} style={{ background: S.surface, borderRadius: 14, width, maxWidth: "90vw", maxHeight: "85vh", overflow: "auto", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", padding: "28px 30px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: S.textTert, padding: 4 }}>{Icons.x}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toast({ message, onDone }) {
  useState(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); });
  return (
    <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 2000, background: S.text, color: "#fff", padding: "12px 20px", borderRadius: S.radius, fontSize: 13, fontWeight: 550, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.2)", animation: "slideUp 0.25s ease" }}>
      <span style={{ color: S.green }}>{Icons.check}</span> {message}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "18px 20px", flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 11, color: S.textTert, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || S.text, letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

// ============================================================================
// INSIGHTS (Intelligence Layer)
// ============================================================================

// Mirrors backend fmt_rate: under 5 in the denominator, counts only — "3 of 4", never "75%".
function fmtRate(r) {
  if (!r || !r.denominator) return "—";
  if (r.small_sample) return `${r.numerator} of ${r.denominator}`;
  return `${Math.round(r.rate * 100)}%`;
}
function fmtDays(m) { return m && m.median_days != null ? `${m.median_days} day${m.median_days === 1 ? "" : "s"}` : "—"; }
function isoDate(d) { return d.toISOString().slice(0, 10); }

const INSIGHT_PRESETS = [
  { id: "since_may", label: "Since May" },
  { id: "90", label: "Last 90 days" },
  { id: "30", label: "Last 30 days" },
  { id: "custom", label: "Custom" },
];

function insightRange(preset, custom) {
  const today = new Date();
  if (preset === "since_may") return { start: "2026-05-01", end: isoDate(today) };
  if (preset === "custom") return { start: custom.start || null, end: custom.end || null };
  const start = new Date(today.getTime() - Number(preset) * 86400000);
  return { start: isoDate(start), end: isoDate(today) };
}

// Fictional, plausible numbers for demo mode only.
function demoInsights(clientId, preset) {
  const k = (clientId ? 0.4 : 1) * (preset === "30" ? 0.3 : preset === "90" ? 0.7 : 1);
  const n = (v) => Math.max(0, Math.round(v * k));
  const reached = [n(64), n(38), n(27), n(15), n(7), n(3), n(5), n(6), n(4)];
  const labels = ["Initial Screening", "Submitted", "Client Screening", "Client Interview", "Client Interview 2", "Client Interview 3", "Client Final Round", "Offer", "Start"];
  const stages = ["initial_screening", "submitted", "client_screening", "client_interview", "client_interview_2", "client_interview_3", "client_final_round", "offer", "start"];
  for (let i = 1; i < reached.length; i++) reached[i] = Math.min(reached[i], reached[i - 1]);
  const rate = (a, b) => ({ numerator: a, denominator: b, rate: b >= 5 ? Math.round((a / b) * 1000) / 1000 : null, small_sample: b < 5 });
  return {
    coverage: { overlaps_gap: preset !== "30", note: preset !== "30" ? "Calendly bookings between May 18 and Sep 24, 2026 were partly lost (some were recovered), so counts covering that window undercount." : null, earliest_record: "2026-05-04T14:00:00+00:00" },
    searches: { opened: n(9), closed: n(5), currently_open: n(6) || 1, closed_by_reason: [
      { key: "filled_by_us", label: "Filled by us", count: n(3) }, { key: "filled_internally", label: "Filled internally by client", count: n(1) },
      { key: "budget", label: "Budget cut / role eliminated", count: n(1) }, { key: "on_hold", label: "Put on hold", count: 0 }, { key: "other", label: "Other", count: 0 }] },
    volume: { entered: reached[0], by_source: { calendly: n(52), manual: n(8), referral: n(4) } },
    funnel: { stages: stages.map((s, i) => ({ stage: s, label: labels[i], reached: reached[i] })),
              conversions: stages.slice(0, -1).map((s, i) => ({ from: s, to: stages[i + 1], ...rate(reached[i + 1], reached[i]) })) },
    speed: { time_in_stage: stages.slice(0, -1).map((s, i) => ({ stage: s, label: labels[i], median_days: [2, 3, 6.5, 5, 7, 4, 3, 2.5][i], n: Math.max(1, Math.round(reached[i] * 0.8)) })),
             time_to_fill: { median_days: 38, n: n(3) } },
    client_review: { completed: { median_days: 9.5, n: n(14) }, waiting_now: { count: n(4), median_days: 6, n: n(4) } },
    stale_in_client_review: clientId && clientId !== "c2" ? [] : [
      { candidate_id: "ca10", name: "Ravi Shankar", role: "VP of Engineering", client: "Vantage Financial", stage: "Client Screening", days_since_activity: 47 },
    ],
    outcomes: { offer_to_start: rate(reached[8], reached[7]),
      rejections_by_stage: [{ stage: "Initial Screening", count: n(14) }, { stage: "Client Screening", count: n(6) }, { stage: "Client Interview", count: n(4) }],
      withdrawals_by_reason: [{ reason: "Accepted another offer", count: n(5) }, { reason: "Compensation", count: n(3) }, { reason: "Unresponsive / ghosted", count: n(2) }] },
    bench: { current_size: n(7), benched_in_range: n(5) },
    placements: [],
  };
}

function Card({ title, children, style }) {
  return (
    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "20px 22px", ...style }}>
      <div style={{ fontSize: 13, fontWeight: 650, color: S.text, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function BarRow({ label, value, max, display, sub, color }) {
  const pct = max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 70px", alignItems: "center", gap: 10, padding: "5px 0" }}>
      <span style={{ fontSize: 12.5, color: S.textSec, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      <div style={{ background: S.borderLight, borderRadius: 4, height: 10 }}>
        <div style={{ width: `${pct}%`, height: 10, borderRadius: 4, background: color || "#a8a29e" }} />
      </div>
      <span style={{ fontSize: 12.5, color: S.text, fontWeight: 600, textAlign: "right" }}>{display ?? value}{sub && <span style={{ color: S.textTert, fontWeight: 400 }}> {sub}</span>}</span>
    </div>
  );
}

function BarList({ items, empty }) {
  const max = Math.max(0, ...items.map(i => i.count));
  if (!items.length || max === 0) return <p style={{ fontSize: 12.5, color: S.textTert, margin: 0 }}>{empty}</p>;
  return items.filter(i => i.count > 0).map(i => <BarRow key={i.label} label={i.label} value={i.count} max={max} />);
}

function InsightsView({ clients, mode, showToast, nav }) {
  const [clientId, setClientId] = useState("");
  const [preset, setPreset] = useState("since_may");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const range = insightRange(preset, custom);

  useEffect(() => {
    let cancelled = false;
    if (mode !== "live") { setData(demoInsights(clientId, preset)); return; }
    if (preset === "custom" && (!custom.start || !custom.end)) return;
    setLoading(true); setError(null);
    api.fetchInsights({ clientId: clientId || null, start: range.start, end: range.end })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, clientId, preset, range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const openClientReport = async () => {
    if (mode !== "live") { showToast("Client reports need live mode"); return; }
    const win = window.open("", "_blank");  // open synchronously so popup blockers allow it
    if (!win) { showToast("Allow pop-ups to open the report"); return; }
    win.document.write("<p style='font-family:Calibri,Arial,sans-serif;padding:24px'>Preparing report…</p>");
    try {
      const html = await api.fetchClientInsightReport(clientId, range);
      win.document.open(); win.document.write(html); win.document.close();
    } catch (e) {
      win.close(); showToast("Report failed: " + e.message);
    }
  };

  const activeClients = clients.filter(c => c.is_active !== false);
  const selectStyle = { fontSize: 13, padding: "7px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.surface, color: S.text, fontFamily: S.font, outline: "none" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Insights</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={clientId} onChange={e => setClientId(e.target.value)} style={selectStyle}>
            <option value="">All clients</option>
            {activeClients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <div style={{ display: "flex", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, overflow: "hidden" }}>
            {INSIGHT_PRESETS.map(p => (
              <button key={p.id} onClick={() => setPreset(p.id)} style={{ fontSize: 12.5, padding: "7px 11px", border: "none", cursor: "pointer", fontFamily: S.font,
                background: preset === p.id ? S.text : S.surface, color: preset === p.id ? "#fff" : S.textSec, fontWeight: preset === p.id ? 600 : 500 }}>{p.label}</button>
            ))}
          </div>
          {preset === "custom" && <>
            <input type="date" value={custom.start} onChange={e => setCustom(c => ({ ...c, start: e.target.value }))} style={selectStyle} />
            <input type="date" value={custom.end} onChange={e => setCustom(c => ({ ...c, end: e.target.value }))} style={selectStyle} />
          </>}
          {clientId && <Btn variant="primary" onClick={openClientReport}>Client report</Btn>}
        </div>
      </div>

      {error && <div style={{ fontSize: 13, color: S.red, marginBottom: 12 }}>Couldn't load insights: {error}</div>}
      {loading && !data && <div style={{ fontSize: 13, color: S.textTert }}>Loading…</div>}
      {data && <InsightsBody data={data} nav={nav} />}
    </div>
  );
}

function InsightsBody({ data, nav }) {
  const f = data.funnel;
  const top = f.stages[0]?.reached || 0;
  const timed = data.speed.time_in_stage.filter(t => t.n > 0);
  const maxDays = Math.max(0, ...timed.map(t => t.median_days));
  const placements = f.stages[f.stages.length - 1]?.reached ?? 0;
  const cr = data.client_review;
  return (
    <div>
      {data.coverage?.overlaps_gap && (
        <div style={{ background: S.amberSoft, border: "1px solid #fde68a", borderRadius: S.radiusSm, padding: "10px 14px", fontSize: 12.5, color: "#92400e", marginBottom: 16 }}>
          <strong>Data coverage:</strong> {data.coverage.note}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <StatCard label="Searches opened" value={data.searches.opened} />
        <StatCard label="Roles open now" value={data.searches.currently_open} />
        <StatCard label="Candidates entered" value={data.volume.entered} />
        <StatCard label="Reached start" value={placements} />
        <StatCard label="Median time to fill" value={fmtDays(data.speed.time_to_fill)} />
        <StatCard label="Offers accepted" value={fmtRate(data.outcomes.offer_to_start)} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Card title="Funnel — candidates who reached each stage">
          {f.stages.map((st, i) => {
            const conv = i > 0 ? f.conversions[i - 1] : null;
            return <BarRow key={st.stage} label={st.label} value={st.reached} max={top} color={S.text}
              sub={conv && conv.denominator ? `· ${fmtRate(conv)}` : ""} />;
          })}
          <p style={{ fontSize: 11, color: S.textTert, margin: "8px 0 0" }}>Conversion from the previous stage; counts shown when fewer than 5 candidates.</p>
        </Card>
        <Card title="Time in stage (median days)">
          {timed.length ? timed.map(t => <BarRow key={t.stage} label={t.label} value={t.median_days} max={maxDays} display={t.median_days} sub={`(${t.n})`} color={S.blue} />)
            : <p style={{ fontSize: 12.5, color: S.textTert, margin: 0 }}>No completed stages in this range yet.</p>}
          <div style={{ borderTop: `1px solid ${S.borderLight}`, marginTop: 10, paddingTop: 10, fontSize: 12.5, color: S.textSec }}>
            <strong style={{ color: S.text }}>Time in client review:</strong> {cr.completed.n ? `median ${fmtDays(cr.completed)} (${cr.completed.n} candidates)` : "none completed in this range"}
            {cr.waiting_now.count > 0 && <> · {cr.waiting_now.count} with clients now, median {fmtDays(cr.waiting_now)} so far</>}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        <Card title="Withdrawal reasons">
          <BarList items={data.outcomes.withdrawals_by_reason.map(w => ({ label: w.reason, count: w.count }))} empty="No withdrawals in this range." />
        </Card>
        <Card title="Searches closed, by reason">
          <BarList items={data.searches.closed_by_reason.map(c => ({ label: c.label, count: c.count }))} empty="No searches closed in this range." />
        </Card>
        <Card title="Rejections by stage">
          <BarList items={data.outcomes.rejections_by_stage.map(r => ({ label: r.stage, count: r.count }))} empty="No rejections in this range." />
        </Card>
      </div>
      {data.stale_in_client_review?.length > 0 && (
        <Card title="Likely stale — needs cleanup" style={{ marginTop: 14 }}>
          <p style={{ fontSize: 12, color: S.textSec, margin: "0 0 10px" }}>
            In a client-side stage with no stage change for 30+ days. Left out of "with clients now" (and out of client reports) until they're moved on.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr>{["Candidate", "Role", "Client", "Stage", "Days since activity"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 10.5, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px 6px 0" }}>{h}</th>)}</tr></thead>
            <tbody>{data.stale_in_client_review.map(c => (
              <tr key={c.candidate_id} style={{ borderTop: `1px solid ${S.borderLight}` }}>
                <td style={{ padding: "7px 8px 7px 0" }}><button onClick={() => nav("candidate", c.candidate_id)} style={{ background: "none", border: "none", padding: 0, color: S.blue, fontWeight: 600, cursor: "pointer", fontFamily: S.font, fontSize: 12.5 }}>{c.name}</button></td>
                <td style={{ padding: "7px 8px 7px 0", color: S.textSec }}>{c.role || "—"}</td>
                <td style={{ padding: "7px 8px 7px 0", color: S.textSec }}>{c.client || "—"}</td>
                <td style={{ padding: "7px 8px 7px 0" }}>{c.stage}</td>
                <td style={{ padding: "7px 0", color: S.amber, fontWeight: 650 }}>{c.days_since_activity}d</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}
      <p style={{ fontSize: 11.5, color: S.textTert, marginTop: 14 }}>
        Bench: {data.bench.current_size} now · {data.bench.benched_in_range} benched in this range. Volume by source: {Object.entries(data.volume.by_source).map(([k, v]) => `${k} ${v}`).join(", ") || "—"}.
        {data.coverage?.earliest_record && <> Data since {new Date(data.coverage.earliest_record).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.</>}
      </p>
    </div>
  );
}

// Role modal: benched candidates suggested for this role. Suggestions only.
const DEMO_BENCH_MATCHES = {
  r5: [{ id: "bm1", candidate_id: "ca15", candidate_name: "Hana Lindqvist", fit: "strong", why: "Bench note cites SQL, dashboarding and cohort analysis in Looker — the core of this Data Analyst role." }],
};

function BenchMatchesPanel({ roleId, mode, onOpenCandidate }) {
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    if (mode !== "live") { setMatches(DEMO_BENCH_MATCHES[roleId] || []); return; }
    api.fetchRoleBenchMatches(roleId).then(setMatches).catch(() => setMatches([]));
  }, [roleId, mode]);
  if (matches === null) return null;
  return (
    <div style={{ borderTop: `1px solid ${S.borderLight}`, marginTop: 6, paddingTop: 12, marginBottom: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: S.textSec, marginBottom: 6 }}>Bench matches</div>
      {matches.length === 0 ? <div style={{ fontSize: 12.5, color: S.textTert }}>No benched candidates suggested for this role.</div> : matches.map(m => (
        <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "6px 0", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <button onClick={() => onOpenCandidate(m.candidate_id)} style={{ background: "none", border: "none", padding: 0, color: S.blue, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font }}>{m.candidate_name}</button>
            <div style={{ fontSize: 11.5, color: S.textSec, lineHeight: 1.45 }}>{m.why}</div>
          </div>
          <Badge label={m.fit === "strong" ? "Strong fit" : "Possible fit"} color={m.fit === "strong" ? S.green : S.amber} bg={m.fit === "strong" ? S.greenSoft : S.amberSoft} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// VIEWS
// ============================================================================
function DashboardView({ candidates, roles, clients, activity, nav }) {
  const active = candidates.filter(c => !["start", "rejected", "withdrawn", "benched"].includes(c.stage));
  const stale = active.filter(c => c.days_in_stage >= 3);
  const stageCounts = {};
  active.forEach(c => { stageCounts[c.stage] = (stageCounts[c.stage] || 0) + 1; });
  const actColors = { candidate_created: S.green, stage_changed: S.blue, prep_brief_generated: S.purple, email_sent: S.amber, webhook_received: S.textTert };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Dashboard</h1>
        <p style={{ fontSize: 13, color: S.textSec, marginTop: 4 }}>Sunday, May 3, 2026</p>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard label="Active Clients" value={clients.length} />
        <StatCard label="Open Roles" value={roles.filter(r => r.status === "open").length} />
        <StatCard label="In Pipeline" value={active.length} />
        <StatCard label="Need Attention" value={stale.length} accent={stale.length > 0 ? S.amber : S.green} />
      </div>

      {/* Pipeline bar */}
      <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "20px 22px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 650, color: S.text }}>Pipeline</span>
          <button onClick={() => nav("pipeline")} style={{ fontSize: 12, color: S.blue, background: "none", border: "none", cursor: "pointer", fontFamily: S.font, fontWeight: 550 }}>View board →</button>
        </div>
        <div style={{ display: "flex", gap: 2, height: 32, borderRadius: 6, overflow: "hidden" }}>
          {STAGES.map(st => { const n = stageCounts[st] || 0; return n > 0 ? (
            <div key={st} style={{ flex: n, background: STAGE_BGS[st], display: "flex", alignItems: "center", justifyContent: "center", minWidth: 44, transition: "flex 0.3s" }}>
              <span style={{ fontSize: 10.5, fontWeight: 650, color: STAGE_COLORS[st] }}>{STAGE_LABELS[st]} · {n}</span>
            </div>
          ) : null; })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "20px 22px" }}>
          <span style={{ fontSize: 13, fontWeight: 650, color: S.text, display: "block", marginBottom: 12 }}>Needs Attention</span>
          {stale.length === 0 ? <p style={{ fontSize: 13, color: S.textTert, padding: "10px 0" }}>All candidates are current.</p> : stale.slice(0, 6).map(c => {
            const role = roles.find(r => r.id === c.role_id);
            return (
              <div key={c.id} onClick={() => nav("candidate", c.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 10px", borderRadius: S.radiusSm, cursor: "pointer", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = S.accentSoft} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div><div style={{ fontSize: 13, fontWeight: 550, color: S.text }}>{c.first_name} {c.last_name}</div><div style={{ fontSize: 11, color: S.textTert }}><RoleTitle role={role} /></div></div>
                <Badge label={`${c.days_in_stage}d in ${STAGE_LABELS[c.stage]}`} color={c.days_in_stage >= 5 ? S.red : S.amber} bg={c.days_in_stage >= 5 ? S.redSoft : S.amberSoft} />
              </div>
            );
          })}
        </div>

        <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "20px 22px" }}>
          <span style={{ fontSize: 13, fontWeight: 650, color: S.text, display: "block", marginBottom: 12 }}>Activity</span>
          {activity.slice(0, 7).map(a => (
            <div key={a.id} style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: `1px solid ${S.borderLight}` }}>
              <div style={{ marginTop: 5, color: actColors[a.action] || S.textTert, flexShrink: 0 }}>{Icons.dot}</div>
              <div><div style={{ fontSize: 12.5, color: S.text, lineHeight: 1.45 }}>{a.detail}</div><div style={{ fontSize: 10.5, color: S.textTert, marginTop: 1 }}>{timeAgo(a.created_at)}</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PipelineView({ candidates, roles, clients, nav, clientFilter, setClientFilter }) {
  const filtered = clientFilter ? candidates.filter(c => { const r = roles.find(rl => rl.id === c.role_id); return r?.client_id === clientFilter; }) : candidates;
  // Rejected candidates don't get a column; Start and Withdrawn do, so they stay on the board.
  const active = filtered.filter(c => c.stage !== "rejected");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Pipeline</h1>
        <select onChange={e => setClientFilter(e.target.value || null)} value={clientFilter || ""} style={{ fontSize: 13, padding: "7px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.surface, color: S.text, fontFamily: S.font, cursor: "pointer", outline: "none" }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 12 }}>
        {PIPELINE_BOARD_STAGES.map(stage => {
          const cards = active.filter(c => c.stage === stage);
          return (
            <div key={stage} style={{ minWidth: 225, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 2px", marginBottom: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: STAGE_COLORS[stage] }} />
                <span style={{ fontSize: 12, fontWeight: 650, color: S.text }}>{STAGE_LABELS[stage]}</span>
                <span style={{ fontSize: 11, color: S.textTert, fontWeight: 500 }}>{cards.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: 100 }}>
                {cards.map(c => {
                  const role = roles.find(r => r.id === c.role_id);
                  const client = clients.find(cl => cl.id === role?.client_id);
                  return (
                    <div key={c.id} onClick={() => nav("candidate", c.id)} style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "13px 15px", cursor: "pointer", transition: "all 0.12s", boxShadow: S.shadow }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#c4c0ba"; e.currentTarget.style.boxShadow = S.shadowMd; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.boxShadow = S.shadow; }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: S.text, marginBottom: 3 }}>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize: 11, color: S.textSec, marginBottom: 8 }}><RoleTitle role={role} /></div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 10.5, color: S.textTert, fontWeight: 500 }}>{client?.company_name}</span>
                        {c.ai_recommendation && <Badge label={recLabels[c.ai_recommendation]} color={recColors[c.ai_recommendation]} bg={recBgs[c.ai_recommendation]} />}
                      </div>
                      {!["start", "withdrawn"].includes(c.stage) && c.days_in_stage >= 3 && <div style={{ marginTop: 7, fontSize: 10.5, color: S.amber, fontWeight: 600 }}>⏳ {c.days_in_stage}d in stage</div>}
                      {(c.phone || c.linkedin_url) && (
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                          {c.phone && <span style={{ color: S.textTert, display: "inline-flex" }} title={c.phone}>{Icons.phone}</span>}
                          {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: S.textTert, display: "inline-flex" }} title="LinkedIn profile">{Icons.linkedin}</a>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {cards.length === 0 && <div style={{ border: `1px dashed ${S.border}`, borderRadius: S.radius, padding: 20, textAlign: "center", color: S.textTert, fontSize: 12 }}>Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Pipeline order for the Stage column sort — not alphabetical.
const STAGE_SORT_ORDER = ["new", "initial_screening", "submitted", "client_screening", "client_interview", "client_interview_2", "client_interview_3", "client_final_round", "offer", "start", "benched", "rejected", "withdrawn"];
const AI_READ_SORT_ORDER = { strong_yes: 0, yes: 1, maybe: 2, no: 3, strong_no: 4 };

const CANDIDATE_SORT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role" },
  { key: "client", label: "Client" },
  { key: "stage", label: "Stage" },
  { key: "days", label: "Days" },
  { key: "ai_read", label: "AI Read", title: "AI-generated fit recommendation based on prep brief and interview data" },
  { label: "LinkedIn" }, // not sortable
];

function candidateSortValue(c, key, roles, clients) {
  const role = roles.find(r => r.id === c.role_id);
  const client = clients.find(cl => cl.id === role?.client_id);
  switch (key) {
    case "name": return (c.last_name || "").toLowerCase();
    case "role": return (role?.title || NEEDS_ROLE).toLowerCase();
    case "client": return (client?.company_name || "").toLowerCase();
    case "stage": {
      const idx = STAGE_SORT_ORDER.indexOf(c.stage);
      return idx === -1 ? STAGE_SORT_ORDER.length : idx;
    }
    case "days": return c.days_in_stage ?? 0;
    // null/no recommendation sorts last regardless of direction (see toggle below).
    case "ai_read": return c.ai_recommendation ? (AI_READ_SORT_ORDER[c.ai_recommendation] ?? 5) : 6;
    default: return 0;
  }
}

function CandidatesView({ candidates, roles, clients, nav, onAdd }) {
  const [search, setSearch] = useState("");
  const [stageF, setStageF] = useState("active");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const filtered = candidates.filter(c => {
    const ms = !search || `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase());
    const mst = stageF === "all" || (stageF === "active" && !["start", "rejected", "withdrawn", "benched"].includes(c.stage)) || c.stage === stageF;
    return ms && mst;
  });

  // Default sort (no header clicked yet) keeps the incoming order — stage_updated_at
  // desc from the API/demo data. Clicking a header sorts purely client-side.
  const sorted = sort.key ? [...filtered].sort((a, b) => {
    const va = candidateSortValue(a, sort.key, roles, clients);
    const vb = candidateSortValue(b, sort.key, roles, clients);
    const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
    return sort.dir === "asc" ? cmp : -cmp;
  }) : filtered;

  const handleSort = (key) => {
    if (!key) return;
    setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Candidates</h1>
        <Btn variant="primary" onClick={onAdd}>{Icons.plus} Add Candidate</Btn>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: S.textTert }}>{Icons.search}</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." style={{ width: "100%", padding: "9px 12px 9px 36px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, fontSize: 13, fontFamily: S.font, outline: "none", background: S.surface, color: S.text }} />
        </div>
        <select value={stageF} onChange={e => setStageF(e.target.value)} style={{ fontSize: 13, padding: "7px 12px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.surface, color: S.text, fontFamily: S.font, cursor: "pointer", outline: "none" }}>
          <option value="active">Active</option><option value="all">All</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: S.font }}>
          <thead><tr style={{ borderBottom: `1px solid ${S.border}` }}>
            {CANDIDATE_SORT_COLUMNS.map(col => (
              <th
                key={col.label}
                title={col.title}
                onClick={col.key ? () => handleSort(col.key) : undefined}
                onMouseEnter={col.key ? e => { e.currentTarget.style.background = S.accentSoft; } : undefined}
                onMouseLeave={col.key ? e => { e.currentTarget.style.background = "transparent"; } : undefined}
                style={{
                  padding: "10px 16px", textAlign: "left", fontSize: 10.5, fontWeight: 650, color: S.textTert,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  cursor: col.key ? "pointer" : "default", userSelect: "none", transition: "background 0.1s",
                }}
              >
                {col.label}{sort.key === col.key && (sort.dir === "asc" ? " ▲" : " ▼")}
              </th>
            ))}
          </tr></thead>
          <tbody>{sorted.map(c => {
            const role = roles.find(r => r.id === c.role_id); const client = clients.find(cl => cl.id === role?.client_id);
            return (
              <tr key={c.id} onClick={() => nav("candidate", c.id)} style={{ borderBottom: `1px solid ${S.borderLight}`, cursor: "pointer", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = S.accentSoft} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "11px 16px" }}>
                  <div style={{ fontWeight: 550, color: S.text, display: "flex", alignItems: "center", gap: 6 }}>
                    {c.first_name} {c.last_name}
                    {c.trust_score != null && c.trust_score < 40 && !c.integrity_dismissed && (
                      <span title={`Trust score ${c.trust_score} — flagged for review`} style={{ display: "inline-flex", width: 7, height: 7, borderRadius: "50%", background: S.red, flexShrink: 0 }} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: S.textTert, marginTop: 1 }}>{c.email}</div>
                  {c.phone && <div style={{ fontSize: 11, color: S.textTert, marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>{Icons.phone}{c.phone}</div>}
                </td>
                <td style={{ padding: "11px 16px", color: S.textSec }}><RoleTitle role={role} /></td>
                <td style={{ padding: "11px 16px", color: S.textSec }}>{client?.company_name}</td>
                <td style={{ padding: "11px 16px" }}><Badge label={STAGE_LABELS[c.stage]} color={STAGE_COLORS[c.stage]} bg={STAGE_BGS[c.stage]} /></td>
                <td style={{ padding: "11px 16px", color: c.days_in_stage >= 3 ? S.amber : S.textTert, fontWeight: c.days_in_stage >= 3 ? 650 : 400 }}>{c.days_in_stage}d</td>
                <td style={{ padding: "11px 16px" }} title="AI-generated fit recommendation based on prep brief and interview data">{c.ai_recommendation ? <Badge label={recLabels[c.ai_recommendation]} color={recColors[c.ai_recommendation]} bg={recBgs[c.ai_recommendation]} /> : <span style={{ color: S.textTert }}>—</span>}</td>
                <td style={{ padding: "11px 16px" }}>{c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: S.textTert, display: "inline-flex" }} title="LinkedIn profile">{Icons.linkedin}</a> : <span style={{ color: S.textTert }}>—</span>}</td>
              </tr>
            );
          })}</tbody>
        </table>
        {filtered.length === 0 && <div style={{ textAlign: "center", padding: "40px 20px", color: S.textTert, fontSize: 13 }}>No candidates match your search.</div>}
      </div>
    </div>
  );
}

function ResumeCard({ candidate: c, onUpload, onDownload }) {
  const [status, setStatus] = useState("idle"); // idle | scanning | error
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setStatus("scanning");
    setError(null);
    try {
      await onUpload(c.id, file);
      setStatus("idle");
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "22px 24px" }}>
      <h3 style={{ fontSize: 11, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>Resume</h3>
      {c.resume_path ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, color: S.text, fontWeight: 500 }}>On file</div>
            <div style={{ fontSize: 11.5, color: S.textTert, marginTop: 2 }}>{c.resume_uploaded_at ? `Uploaded ${fmtDate(c.resume_uploaded_at)}` : ""}</div>
          </div>
          <Btn onClick={() => onDownload(c.id)}>Download</Btn>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: S.textTert, marginBottom: 14 }}>No resume on file.</div>
      )}
      {c.resume_jd_similarity != null && c.resume_jd_similarity > 0.85 && (
        <div style={{ fontSize: 12, color: S.textSec, marginBottom: 14, fontStyle: "italic" }}>
          High textual similarity to the job description ({Math.round(c.resume_jd_similarity * 100)}%).
        </div>
      )}
      <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: "none" }} onChange={handleFile} />
      <Btn onClick={() => fileRef.current?.click()} disabled={status === "scanning"} style={{ opacity: status === "scanning" ? 0.6 : 1 }}>
        {status === "scanning" ? "Scanning..." : c.resume_path ? "Replace resume" : "Upload resume"}
      </Btn>
      {status === "error" && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: S.red, padding: "8px 12px", background: S.redSoft, borderRadius: S.radiusSm }}>{error}</div>
      )}
    </div>
  );
}

// Inline-editable detail row (email / phone / LinkedIn). onSave returns a
// promise; a thrown Error's message is shown under the field.
function EditableRow({ label, icon, value, display, href, placeholder, type = "text", validate, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const start = () => { setDraft(value || ""); setError(null); setEditing(true); };
  const cancel = () => { setEditing(false); setError(null); };
  const save = async () => {
    const v = draft.trim();
    const problem = validate ? validate(v) : null;
    if (problem) { setError(problem); return; }
    setSaving(true);
    try { await onSave(v); setEditing(false); setError(null); }
    catch (err) { setError(err.message || "Save failed"); }
    finally { setSaving(false); }
  };
  const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${S.borderLight}` };
  const labelEl = <span style={{ fontSize: 13, color: S.textSec, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>{icon && <span style={{ color: S.textTert }}>{icon}</span>}{label}</span>;
  if (editing) {
    return (
      <div style={{ padding: "8px 0", borderBottom: `1px solid ${S.borderLight}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {labelEl}
          <input autoFocus type={type} value={draft} placeholder={placeholder} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            style={{ flex: 1, minWidth: 0, padding: "5px 8px", border: `1px solid ${error ? S.red : S.border}`, borderRadius: S.radiusSm, fontSize: 13, fontFamily: S.font, color: S.text, outline: "none" }} />
          <Btn variant="primary" onClick={save} disabled={saving} style={{ fontSize: 12, padding: "5px 10px" }}>{saving ? "Saving..." : "Save"}</Btn>
          <Btn variant="ghost" onClick={cancel} style={{ fontSize: 12, padding: "5px 8px" }}>Cancel</Btn>
        </div>
        {error && <div style={{ fontSize: 11.5, color: S.red, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }
  return (
    <div style={rowStyle}>
      {labelEl}
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {href
          ? <a href={href} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: S.blue, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</a>
          : <span style={{ fontSize: 13, color: S.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</span>}
        <button onClick={start} title={`Edit ${label.toLowerCase()}`} style={{ background: "none", border: "none", color: S.textTert, fontSize: 12, cursor: "pointer", fontFamily: S.font, padding: 0 }}>Edit</button>
      </span>
    </div>
  );
}

function AssignRoleCard({ candidate: c, roles, clients, onAssignRole }) {
  const [roleId, setRoleId] = useState("");
  const [saving, setSaving] = useState(false);
  const assignable = roles.filter(r => r.status !== "closed");
  const byClient = clients
    .map(cl => ({ client: cl, roles: assignable.filter(r => r.client_id === cl.id).sort((a, b) => a.title.localeCompare(b.title)) }))
    .filter(g => g.roles.length > 0);
  const assign = async () => {
    if (!roleId) return;
    setSaving(true);
    try { await onAssignRole(c.id, roleId); } finally { setSaving(false); }
  };
  return (
    <div style={{ background: S.amberSoft, border: `1px solid ${S.amber}`, borderRadius: S.radius, padding: "16px 20px", marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 650, color: S.text, marginBottom: 4 }}>This booking needs a role</div>
      <div style={{ fontSize: 13, color: S.textSec, marginBottom: 12 }}>
        Their Calendly role answer {c.unmatched_role_answer ? <>was <strong style={{ color: S.text }}>&ldquo;{c.unmatched_role_answer}&rdquo;</strong></> : "was blank"}, which didn't match any role.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select value={roleId} onChange={e => setRoleId(e.target.value)} style={{ flex: 1, maxWidth: 420, fontSize: 13, padding: "7px 10px", border: `1px solid ${S.border}`, borderRadius: S.radiusSm, background: S.surface, color: S.text, fontFamily: S.font, outline: "none" }}>
          <option value="">Select a role...</option>
          {byClient.map(g => (
            <optgroup key={g.client.id} label={g.client.company_name}>
              {g.roles.map(r => <option key={r.id} value={r.id}>{r.title}{r.status !== "open" ? ` (${r.status})` : ""}</option>)}
            </optgroup>
          ))}
        </select>
        <Btn variant="primary" onClick={assign} disabled={!roleId || saving}>{saving ? "Assigning..." : "Assign"}</Btn>
      </div>
    </div>
  );
}

function CandidateDetailView({ candidate: c, roles, clients, onBack, backLabel, onStageChange, onWithdraw, onBench, mode, onUploadResume, onDownloadResume, onAssignRole, onUpdateFields }) {
  const role = roles.find(r => r.id === c.role_id);
  const client = clients.find(cl => cl.id === role?.client_id);
  const benchedFromRole = c.stage === "benched" ? roles.find(r => r.id === (c.benched_from_role_id || c.role_id)) : null;
  const benchedFromClient = benchedFromRole ? clients.find(cl => cl.id === benchedFromRole.client_id) : null;
  const [tab, setTab] = useState("overview");
  const tabs = [{ id: "overview", label: "Overview" }, { id: "prep", label: "Prep Brief" }, { id: "timeline", label: "Timeline" }];
  const moveableStages = MOVE_STAGES.filter(s => s !== c.stage);

  return (
    <div>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: S.textSec, fontSize: 13, fontFamily: S.font, cursor: "pointer", padding: "0 0 18px 0", fontWeight: 500 }}>{Icons.back} {backLabel || "Back to candidates"}</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>{c.first_name} {c.last_name}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, fontSize: 13, color: S.textSec }}><RoleTitle role={role} />{client && <><span style={{ color: S.border }}>·</span>{client.company_name}</>}</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Badge label={STAGE_LABELS[c.stage]} color={STAGE_COLORS[c.stage]} bg={STAGE_BGS[c.stage]} />
          {c.ai_recommendation && <Badge label={recLabels[c.ai_recommendation]} color={recColors[c.ai_recommendation]} bg={recBgs[c.ai_recommendation]} />}
          <TrustBadge score={c.trust_score} signals={c.integrity_signals} />
        </div>
      </div>

      {!c.role_id && <AssignRoleCard candidate={c} roles={roles} clients={clients} onAssignRole={onAssignRole} />}

      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${S.border}`, marginBottom: 22 }}>
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "10px 18px", fontSize: 13, fontWeight: tab === t.id ? 650 : 450, color: tab === t.id ? S.text : S.textSec, borderBottom: tab === t.id ? `2px solid ${S.text}` : "2px solid transparent", background: "none", border: "none", cursor: "pointer", fontFamily: S.font, marginBottom: -1 }}>{t.label}</button>)}
      </div>

      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "22px 24px" }}>
            <h3 style={{ fontSize: 11, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>Details</h3>
            <EditableRow label="Email" icon={Icons.mail} value={c.email} display={c.email} type="email" placeholder="name@example.com"
              validate={v => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Enter a valid email address")}
              onSave={v => onUpdateFields(c.id, { email: v })} />
            <EditableRow label="Phone" value={c.phone} display={c.phone || "—"} type="tel" placeholder="(555) 555-0100"
              onSave={v => onUpdateFields(c.id, { phone: v })} />
            <EditableRow label="LinkedIn" icon={Icons.link} value={c.linkedin_url} display={c.linkedin_url ? "View profile" : "—"} href={c.linkedin_url || null}
              placeholder="linkedin.com/in/name"
              validate={v => (!v || normalizeLinkedIn(v) ? null : "Not a LinkedIn profile URL — expected linkedin.com/in/<name>")}
              onSave={v => onUpdateFields(c.id, { linkedin_url: v ? normalizeLinkedIn(v) : "" })} />
            {[
              { l: "Source", v: c.source },
              { l: "Days in Stage", v: `${c.days_in_stage} days` },
              { l: "Scheduled", v: c.scheduled_at ? fmtDate(c.scheduled_at) : "—", icon: Icons.clock },
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${S.borderLight}` }}>
                <span style={{ fontSize: 13, color: S.textSec, display: "flex", alignItems: "center", gap: 6 }}>{f.icon && <span style={{ color: S.textTert }}>{f.icon}</span>}{f.l}</span>
                <span style={{ fontSize: 13, color: f.isLink ? S.blue : S.text, fontWeight: 500 }}>{f.v}</span>
              </div>
            ))}
            {c.stage === "benched" && (
              <div style={{ marginTop: 14, padding: "12px 14px", background: S.purpleSoft, borderRadius: S.radiusSm }}>
                <div style={{ fontSize: 11, fontWeight: 650, color: S.purple, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>On The Bench</div>
                <div style={{ fontSize: 13, color: S.text, marginBottom: 6 }}>{c.bench_reason || "No reason recorded."}</div>
                <div style={{ fontSize: 12, color: S.textSec }}>Benched from {benchedFromRole?.title || "—"}{benchedFromClient && ` at ${benchedFromClient.company_name}`}</div>
                <div style={{ fontSize: 12, color: S.textSec, marginTop: 2 }}>Follow up: {c.bench_follow_up_at ? fmtDate(c.bench_follow_up_at) : "—"}</div>
              </div>
            )}
          </div>
          <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "22px 24px" }}>
            <h3 style={{ fontSize: 11, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>Move Stage</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {moveableStages.map(s => (
                <Btn key={s} onClick={() => s === "withdrawn" ? onWithdraw(c.id) : s === "benched" ? onBench(c.id) : onStageChange(c.id, s)} style={{ width: "100%", justifyContent: "flex-start" }}>
                  Move to {s === "withdrawn" ? "Withdraw" : s === "benched" ? "Bench" : STAGE_LABELS[s]}
                </Btn>
              ))}
              <div style={{ height: 8 }} />
              <Btn variant="danger" onClick={() => onStageChange(c.id, "rejected")} style={{ width: "100%" }}>Reject Candidate</Btn>
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <ResumeCard candidate={c} onUpload={onUploadResume} onDownload={onDownloadResume} />
          </div>
        </div>
      )}

      {tab === "prep" && (
        <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "28px 32px" }}>
          {c.prep_brief ? (
            <div style={{ fontSize: 14, color: S.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              {c.prep_brief.split(/(\*\*.*?\*\*)/).map((part, i) => part.startsWith("**") ? <strong key={i}>{part.replace(/\*\*/g, "")}</strong> : <span key={i}>{part}</span>)}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 20px", color: S.textTert }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: S.textSec, marginBottom: 4 }}>No prep brief yet</p>
              <p style={{ fontSize: 13 }}>The prep brief is generated automatically when a candidate books via Calendly.</p>
            </div>
          )}
        </div>
      )}

      {tab === "timeline" && (
        <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "22px 24px" }}>
          {[
            { a: `Currently in ${STAGE_LABELS[c.stage]}`, t: `${c.days_in_stage} days`, who: "" },
            ...(c.prep_brief ? [{ a: "Prep brief generated by Claude", t: "Auto", who: "system" }] : []),
            { a: `Candidate created from ${c.source === "calendly" ? "Calendly booking" : c.source}`, t: "Initial", who: c.source === "calendly" ? "webhook" : "manual" },
          ].map((item, i, arr) => (
            <div key={i} style={{ display: "flex", gap: 14, paddingBottom: 18, position: "relative" }}>
              {i < arr.length - 1 && <div style={{ position: "absolute", left: 5, top: 14, bottom: 0, width: 1, background: S.border }} />}
              <div style={{ width: 11, height: 11, borderRadius: "50%", border: `2px solid ${i === 0 ? S.blue : S.border}`, background: i === 0 ? S.blueSoft : S.surface, flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, color: S.text, fontWeight: 500 }}>{item.a}</div>
                <div style={{ fontSize: 11, color: S.textTert, marginTop: 2 }}>{item.t}{item.who && ` · ${item.who}`}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RolesView({ roles, clients, candidates, nav, onAdd, onEdit, onCloseRole, onReopenRole }) {
  const [showClosed, setShowClosed] = useState(false);
  const visibleRoles = roles.filter(r => showClosed || r.status !== "closed");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Roles</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: S.textSec, cursor: "pointer", fontWeight: 500 }}>
            <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} />
            Show closed roles
          </label>
          <Btn variant="primary" onClick={onAdd}>{Icons.plus} Add Role</Btn>
        </div>
      </div>
      <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: S.font }}>
          <thead><tr style={{ borderBottom: `1px solid ${S.border}` }}>
            {["Role", "Client", "Location", "Pipeline", "Calendly Label", "Status", ""].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10.5, fontWeight: 650, color: S.textTert, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>)}
          </tr></thead>
          <tbody>{visibleRoles.map(r => {
            const client = clients.find(c => c.id === r.client_id);
            const n = candidates.filter(c => c.role_id === r.id && !["start", "rejected", "withdrawn", "benched"].includes(c.stage)).length;
            const closed = r.status === "closed";
            return (
              <tr key={r.id} style={{ borderBottom: `1px solid ${S.borderLight}`, opacity: closed ? 0.55 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = S.accentSoft} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", cursor: "pointer" }}><div style={{ fontWeight: 550, color: S.text }}>{r.title}</div><div style={{ fontSize: 11, color: S.textTert, marginTop: 1 }}>{r.department}</div></td>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", color: S.textSec, cursor: "pointer" }}>{client?.company_name}</td>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", color: S.textSec, cursor: "pointer" }}>{r.location}</td>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", cursor: "pointer" }}><strong style={{ color: S.text }}>{n}</strong> <span style={{ color: S.textTert }}>active</span></td>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", cursor: "pointer" }}><code style={{ fontSize: 11.5, color: S.purple, background: S.purpleSoft, padding: "2px 7px", borderRadius: 4 }}>{r.calendly_label}</code></td>
                <td onClick={() => onEdit(r)} style={{ padding: "13px 16px", cursor: "pointer" }}>
                  <Badge label={r.status} color={r.status === "open" ? S.green : S.textTert} bg={r.status === "open" ? S.greenSoft : S.accentSoft} />
                  {closed && r.close_reason && <div style={{ fontSize: 10.5, color: S.textTert, marginTop: 4 }}>{r.close_reason}</div>}
                </td>
                <td style={{ padding: "13px 16px" }}>
                  {!closed && <Btn onClick={() => onCloseRole(r)} style={{ fontSize: 12, padding: "5px 10px" }}>Close Role</Btn>}
                  {closed && <Btn onClick={() => onReopenRole(r)} style={{ fontSize: 12, padding: "5px 10px" }}>Reopen Role</Btn>}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
}

function ClientsView({ clients, roles, candidates, nav, onAdd }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: S.text, margin: 0, letterSpacing: "-0.02em" }}>Clients</h1>
        <Btn variant="primary" onClick={onAdd}>{Icons.plus} Add Client</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 12 }}>
        {clients.map(c => {
          const cRoles = roles.filter(r => r.client_id === c.id && r.status === "open");
          const cCands = candidates.filter(cd => { const r = roles.find(rl => rl.id === cd.role_id); return r?.client_id === c.id && !["start","rejected","withdrawn","benched"].includes(cd.stage); });
          return (
            <div key={c.id} onClick={() => { nav("pipeline"); }} style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: S.radius, padding: "22px 24px", cursor: "pointer", transition: "all 0.12s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#c4c0ba"; e.currentTarget.style.boxShadow = S.shadowMd; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <div><div style={{ fontSize: 16, fontWeight: 650, color: S.text, letterSpacing: "-0.01em" }}>{c.company_name}</div><div style={{ fontSize: 12, color: S.textSec, marginTop: 3 }}>{c.name} · {c.industry}</div></div>
                <div style={{ width: 36, height: 36, borderRadius: S.radiusSm, background: S.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: S.textSec }}>{c.company_name[0]}</div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: S.textSec }}>
                <span><strong style={{ color: S.text }}>{cRoles.length}</strong> roles</span>
                <span><strong style={{ color: S.text }}>{cCands.length}</strong> candidates</span>
                <span>{c.headcount} employees</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN APP
// ============================================================================
// ============================================================================
// LOGIN SCREEN
// ============================================================================
function LoginScreen({ onLogin, onDemo, error, loading }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const handleSubmit = (e) => { e.preventDefault(); onLogin(email, password); };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontFamily: S.font, background: S.bg }}>
      <div style={{ width: 380, padding: "44px 36px", background: S.surface, border: `1px solid ${S.border}`, borderRadius: 14, boxShadow: S.shadowMd }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: S.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#fff" }}>R</div>
          <span style={{ fontSize: 18, fontWeight: 700, color: S.text, letterSpacing: "-0.025em" }}>Recruiting OS</span>
        </div>
        <form onSubmit={handleSubmit}>
          <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" autoFocus />
          <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
          {error && <div style={{ fontSize: 12.5, color: S.red, marginBottom: 14, padding: "8px 12px", background: S.redSoft, borderRadius: S.radiusSm }}>{error}</div>}
          <Btn variant="primary" style={{ width: "100%", justifyContent: "center", padding: "11px 14px", marginBottom: 10 }} onClick={() => onLogin(email, password)} disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </Btn>
        </form>
        <div style={{ textAlign: "center", paddingTop: 8, borderTop: `1px solid ${S.borderLight}`, marginTop: 10 }}>
          <button onClick={onDemo} style={{ fontSize: 13, color: S.textSec, background: "none", border: "none", cursor: "pointer", fontFamily: S.font, fontWeight: 500, padding: "8px 0" }}>
            Continue in demo mode →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN APP WITH API INTEGRATION
// ============================================================================
export default function App() {
  // Mode: "checking" | "login" | "demo" | "live"
  const [mode, setMode] = useState("checking");
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [view, setView] = useState("dashboard");
  const [selectedId, setSelectedId] = useState(null);
  const [candidateOrigin, setCandidateOrigin] = useState("candidates");
  const [clientFilter, setClientFilter] = useState(null);
  const [candidates, setCandidates] = useState(INITIAL_CANDIDATES);
  const [clients, setClients] = useState(INITIAL_CLIENTS);
  const [roles, setRoles] = useState(INITIAL_ROLES);
  const [activity, setActivity] = useState(INITIAL_ACTIVITY);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [user, setUser] = useState(null);
  const refreshTimer = useRef(null);

  // --- Startup: check if backend is available ---
  useEffect(() => {
    async function init() {
      const backendUp = await api.checkHealth();
      if (backendUp && api.isAuthenticated()) {
        try {
          const me = await api.getMe();
          setUser(me);
          setMode("live");
          await refreshData();
        } catch {
          setMode("login");
        }
      } else if (backendUp) {
        setMode("login");
      } else {
        setMode("demo");
      }
    }
    init();

    // Listen for auth expiry
    const handler = () => { setMode("login"); setUser(null); };
    window.addEventListener("auth_expired", handler);
    return () => window.removeEventListener("auth_expired", handler);
  }, []);

  // --- Auto-refresh in live mode (every 30s) ---
  useEffect(() => {
    if (mode === "live") {
      refreshTimer.current = setInterval(() => refreshData(true), 30000);
      return () => clearInterval(refreshTimer.current);
    }
  }, [mode]);

  // --- Fetch all data from API ---
  async function refreshData(silent = false) {
    if (!silent) setLoading(true);
    try {
      const [cl, ro, ca, act] = await Promise.all([
        api.fetchClients(),
        api.fetchRoles(),
        api.fetchCandidates({ activeOnly: false }),
        api.fetchActivityLog(20),
      ]);
      setClients(cl);
      setRoles(ro);
      setCandidates(ca);
      setActivity(act);
    } catch (err) {
      if (!silent) showToast("Failed to load data: " + err.message);
    }
    if (!silent) setLoading(false);
  }

  // --- Login ---
  async function handleLogin(email, password) {
    setLoginError(null);
    setLoginLoading(true);
    try {
      await api.login(email, password);
      const me = await api.getMe();
      setUser(me);
      setMode("live");
      await refreshData();
    } catch (err) {
      setLoginError(err.message);
    }
    setLoginLoading(false);
  }

  // --- Logout ---
  function handleLogout() {
    api.logout();
    setUser(null);
    setMode("login");
    setCandidates(INITIAL_CANDIDATES);
    setClients(INITIAL_CLIENTS);
    setRoles(INITIAL_ROLES);
    setActivity(INITIAL_ACTIVITY);
  }

  // --- Switch to demo ---
  function enterDemo() {
    setMode("demo");
    setCandidates(INITIAL_CANDIDATES);
    setClients(INITIAL_CLIENTS);
    setRoles(INITIAL_ROLES);
    setActivity(INITIAL_ACTIVITY);
  }

  const nav = useCallback((v, id = null) => {
    if (v === "candidate" && view !== "candidate") setCandidateOrigin(view);
    setView(v); setSelectedId(id);
  }, [view]);
  const showToast = (msg) => setToast(msg);
  const BACK_LABELS = { pipeline: "Back to pipeline", candidates: "Back to candidates", dashboard: "Back to dashboard" };

  // --- Actions (API-backed in live mode, local state in demo) ---
  // extra carries bench-specific fields (bench_reason, follow_up_days) — only
  // meaningful when newStage === "benched", ignored otherwise.
  const changeStage = async (candId, newStage, notes = null, dispositionReason = null, extra = {}) => {
    if (mode === "live") {
      try {
        await api.updateCandidateStage(candId, newStage, notes, dispositionReason, extra);
        await refreshData(true);
        showToast(`Moved to ${STAGE_LABELS[newStage]}`);
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      setCandidates(prev => prev.map(c => {
        if (c.id !== candId) return c;
        const old = c.stage;
        setActivity(a => [{ id: `a-${Date.now()}`, action: "stage_changed", detail: `${c.first_name} ${c.last_name}: ${STAGE_LABELS[old]} → ${STAGE_LABELS[newStage]}`, actor: "you@email.com", created_at: new Date().toISOString() }, ...a]);
        const benchFields = newStage === "benched" ? {
          bench_reason: extra.bench_reason || "",
          bench_follow_up_at: new Date(Date.now() + (extra.follow_up_days || 45) * 86400000).toISOString(),
          benched_from_role_id: c.role_id,
        } : {};
        return { ...c, stage: newStage, days_in_stage: 0, ...(dispositionReason ? { disposition_reason: dispositionReason } : {}), ...benchFields };
      }));
      showToast(`Moved to ${STAGE_LABELS[newStage]}`);
    }
  };

  const assignRole = async (candId, roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (mode === "live") {
      try {
        await api.updateCandidate(candId, { role_id: roleId });
        await refreshData(true);
        showToast(`Assigned to ${role?.title || "role"}`);
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      setCandidates(prev => prev.map(c => c.id === candId ? { ...c, role_id: roleId } : c));
      showToast(`Assigned to ${role?.title || "role"}`);
    }
  };

  const addCandidate = async () => {
    const { first_name, last_name, email, role_id } = form;
    if (!first_name || !last_name || !email || !role_id) return;

    if (mode === "live") {
      try {
        await api.createCandidate({ first_name, last_name, email, role_id, phone: form.phone || "", source: "manual" });
        await refreshData(true);
        setModal(null); setForm({}); showToast("Candidate added");
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      const nc = { id: `ca-${Date.now()}`, role_id, first_name, last_name, email, phone: form.phone || "", stage: "initial_screening", source: "manual", days_in_stage: 0, ai_recommendation: null, scheduled_at: null, full_name: `${first_name} ${last_name}` };
      setCandidates(prev => [nc, ...prev]);
      setActivity(a => [{ id: `a-${Date.now()}`, action: "candidate_created", detail: `${first_name} ${last_name} added manually`, actor: "you@email.com", created_at: new Date().toISOString() }, ...a]);
      setModal(null); setForm({}); showToast("Candidate added");
    }
  };

  const addClient = async () => {
    const { company_name, name, contact_email, industry, headcount } = form;
    if (!company_name || !name || !contact_email) return;

    if (mode === "live") {
      try {
        await api.createClient({ company_name, name, contact_email, industry: industry || "", headcount: parseInt(headcount) || 0, report_format: "email" });
        await refreshData(true);
        setModal(null); setForm({}); showToast("Client added");
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      setClients(prev => [...prev, { id: `c-${Date.now()}`, company_name, name, contact_email, industry: industry || "", headcount: parseInt(headcount) || 0, is_active: true }]);
      setModal(null); setForm({}); showToast("Client added");
    }
  };

  const saveRole = async () => {
    const { id, title, client_id, department, location, calendly_label, job_description, key_requirements, screening_questions } = form;
    if (!title || !client_id) return;

    const payload = {
      title,
      department: department || "",
      location: location || "",
      calendly_label: calendly_label || title,
      job_description: job_description || "",
      key_requirements: key_requirements || "",
      screening_questions: screening_questions || "",
    };

    if (mode === "live") {
      try {
        if (id) {
          await api.updateRole(id, payload);
          showToast("Role updated");
        } else {
          await api.createRole({ client_id, ...payload });
          showToast("Role added");
        }
        await refreshData(true);
        setModal(null); setForm({});
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      if (id) {
        setRoles(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
        showToast("Role updated");
      } else {
        setRoles(prev => [...prev, { id: `r-${Date.now()}`, client_id, ...payload, status: "open" }]);
        showToast("Role added");
      }
      setModal(null); setForm({});
    }
  };

  const openWithdrawModal = (candId) => { setForm({ candidate_id: candId, withdrawal_reason: "", notes: "" }); setModal("withdraw"); };

  const confirmWithdraw = async () => {
    const { candidate_id, withdrawal_reason, notes } = form;
    if (!withdrawal_reason) return;
    await changeStage(candidate_id, "withdrawn", notes || null, withdrawal_reason);
    setModal(null); setForm({});
  };

  const openBenchModal = (candId) => { setForm({ candidate_id: candId, bench_reason: "", follow_up_days: 45 }); setModal("bench"); };

  const confirmBench = async () => {
    const { candidate_id, bench_reason, follow_up_days } = form;
    if (!bench_reason) return;
    await changeStage(candidate_id, "benched", null, null, { bench_reason, follow_up_days: parseInt(follow_up_days) || 45 });
    setModal(null); setForm({});
  };

  const openCloseRoleModal = (role) => { setForm({ role_id: role.id, close_reason_type: "", close_reason_notes: "" }); setModal("closeRole"); };

  const reopenRole = async (role) => {
    if (mode === "live") {
      try {
        await api.updateRole(role.id, { status: "open" });
        await refreshData(true);
        showToast(`Reopened ${role.title}`);
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, status: "open", close_reason: null, closed_at: null } : r));
      showToast(`Reopened ${role.title}`);
    }
  };

  // Inline edits from the candidate detail panel. Throws on failure so the
  // field can show the server's message (e.g. invalid LinkedIn URL).
  const updateCandidateFields = async (candId, patch) => {
    if (mode === "live") {
      await api.updateCandidate(candId, patch);
      await refreshData(true);
    } else {
      const local = { ...patch };
      if ("linkedin_url" in local) local.linkedin_url = local.linkedin_url ? normalizeLinkedIn(local.linkedin_url) : null;
      if ("phone" in local) local.phone = local.phone || null;
      setCandidates(prev => prev.map(c => c.id === candId ? { ...c, ...local } : c));
    }
    showToast("Saved");
  };

  const closeRole = async () => {
    const { role_id, close_reason_type, close_reason_notes } = form;
    if (!close_reason_type) return;
    const close_reason = close_reason_notes ? `${close_reason_type} — ${close_reason_notes}` : close_reason_type;

    if (mode === "live") {
      try {
        await api.updateRole(role_id, { status: "closed", close_reason });
        await refreshData(true);
        setModal(null); setForm({}); showToast("Role closed");
      } catch (err) {
        showToast("Failed: " + err.message);
      }
    } else {
      setRoles(prev => prev.map(r => r.id === role_id ? { ...r, status: "closed", close_reason, closed_at: new Date().toISOString() } : r));
      setModal(null); setForm({}); showToast("Role closed");
    }
  };

  // --- Resume upload/download ---
  // Errors are intentionally left to propagate to ResumeCard's own catch
  // block, which renders them inline next to the upload control.
  const uploadResume = async (candId, file) => {
    if (mode === "live") {
      const updated = await api.uploadResume(candId, file);
      setCandidates(prev => prev.map(c => c.id === candId ? updated : c));
      showToast("Resume uploaded and scanned clean");
    } else {
      await new Promise(r => setTimeout(r, 900));
      setCandidates(prev => prev.map(c => c.id === candId ? {
        ...c,
        resume_path: `demo/${file.name}`,
        resume_uploaded_at: new Date().toISOString(),
        resume_jd_similarity: 0.42,
      } : c));
      showToast("Resume uploaded and scanned clean (demo — no real scan performed)");
    }
  };

  const downloadResume = async (candId) => {
    if (mode === "live") {
      try {
        await api.downloadResume(candId);
      } catch (err) {
        showToast("Download failed: " + err.message);
      }
    } else {
      showToast("Demo mode — no real file is stored, so there's nothing to download.");
    }
  };

  const candidate = selectedId ? candidates.find(c => c.id === selectedId) : null;

  // --- Show login screen ---
  if (mode === "checking") {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontFamily: S.font, background: S.bg, color: S.textSec, fontSize: 14 }}>Connecting...</div>;
  }

  if (mode === "login") {
    return <LoginScreen onLogin={handleLogin} onDemo={enterDemo} error={loginError} loading={loginLoading} />;
  }

  const navItems = [
    { id: "dashboard", icon: Icons.home, label: "Dashboard" },
    { id: "pipeline", icon: Icons.pipeline, label: "Pipeline", count: candidates.filter(c => !["start","rejected","withdrawn","benched"].includes(c.stage)).length },
    { id: "candidates", icon: Icons.people, label: "Candidates", count: candidates.length },
    { id: "roles", icon: Icons.briefcase, label: "Roles", count: roles.filter(r => r.status === "open").length },
    { id: "clients", icon: Icons.building, label: "Clients", count: clients.length },
    { id: "insights", icon: Icons.chart, label: "Insights" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: S.font, background: S.bg, color: S.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;450;500;550;600;650;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}::selection{background:#dbeafe}
        ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#d6d3d1;border-radius:3px}
        input:focus,select:focus{border-color:#a8a29e!important;box-shadow:0 0 0 3px rgba(168,162,158,0.12)}
        table{font-family:${S.font}}
        @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
      `}</style>

      {/* Sidebar */}
      <div style={{ width: 220, flexShrink: 0, background: S.surface, borderRight: `1px solid ${S.border}`, padding: "20px 14px", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        <div style={{ padding: "4px 10px 22px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${S.borderLight}`, marginBottom: 16 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: S.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>R</div>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: S.text, letterSpacing: "-0.025em" }}>Recruiting OS</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => nav(item.id)} style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px",
              background: (view === item.id || (view === "candidate" && item.id === "candidates")) ? S.accentSoft : "transparent",
              color: (view === item.id || (view === "candidate" && item.id === "candidates")) ? S.text : S.textSec,
              border: "none", borderRadius: S.radiusSm, cursor: "pointer", fontFamily: S.font, fontSize: 13.5,
              fontWeight: (view === item.id || (view === "candidate" && item.id === "candidates")) ? 600 : 450, textAlign: "left", transition: "all 0.12s",
            }}>
              <span style={{ opacity: (view === item.id || (view === "candidate" && item.id === "candidates")) ? 1 : 0.5, flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.count !== undefined && <span style={{ fontSize: 11, color: S.textTert, fontWeight: 500 }}>{item.count}</span>}
            </button>
          ))}
        </div>
        <div style={{ padding: "14px 10px", borderTop: `1px solid ${S.borderLight}` }}>
          {/* Mode indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "6px 10px", background: mode === "live" ? S.greenSoft : S.amberSoft, borderRadius: S.radiusSm }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: mode === "live" ? S.green : S.amber }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: mode === "live" ? S.green : S.amber }}>{mode === "live" ? "Connected" : "Demo Mode"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#4338ca" }}>
              {user ? user.full_name?.charAt(0).toUpperCase() : "HR"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: S.text }}>{user ? user.full_name : "Demo User"}</div>
              <div style={{ fontSize: 10.5, color: S.textTert }}>{user ? user.email : "demo@example.com"}</div>
            </div>
            {mode === "live" && (
              <button onClick={handleLogout} title="Sign out" style={{ background: "none", border: "none", cursor: "pointer", color: S.textTert, padding: 4, fontSize: 11 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: "32px 40px", maxWidth: 1200, minWidth: 0, position: "relative" }}>
        {loading && (
          <div style={{ position: "absolute", top: 32, right: 40, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: S.textTert }}>
            <div style={{ width: 14, height: 14, border: `2px solid ${S.border}`, borderTopColor: S.blue, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            Loading...
          </div>
        )}
        {view === "dashboard" && <DashboardView candidates={candidates} roles={roles} clients={clients} activity={activity} nav={nav} />}
        {view === "pipeline" && <PipelineView candidates={candidates} roles={roles} clients={clients} nav={nav} clientFilter={clientFilter} setClientFilter={setClientFilter} />}
        {view === "candidates" && <CandidatesView candidates={candidates} roles={roles} clients={clients} nav={nav} onAdd={() => { setForm({}); setModal("candidate"); }} />}
        {view === "candidate" && candidate && <CandidateDetailView candidate={candidate} roles={roles} clients={clients} onBack={() => nav(candidateOrigin)} backLabel={BACK_LABELS[candidateOrigin]} onStageChange={changeStage} onWithdraw={openWithdrawModal} onBench={openBenchModal} mode={mode} onUploadResume={uploadResume} onDownloadResume={downloadResume} onAssignRole={assignRole} onUpdateFields={updateCandidateFields} />}
        {view === "roles" && <RolesView roles={roles} clients={clients} candidates={candidates} nav={nav} onAdd={() => { setForm({}); setModal("role"); }} onEdit={(r) => { setForm({ ...r }); setModal("role"); }} onCloseRole={openCloseRoleModal} onReopenRole={reopenRole} />}
        {view === "insights" && <InsightsView clients={clients} mode={mode} showToast={showToast} nav={nav} />}
        {view === "clients" && <ClientsView clients={clients} roles={roles} candidates={candidates} nav={nav} onAdd={() => { setForm({}); setModal("client"); }} />}
      </div>

      {/* Modals */}
      {modal === "candidate" && (
        <Modal title="Add Candidate" onClose={() => setModal(null)}>
          <Input label="First Name" value={form.first_name || ""} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Jordan" />
          <Input label="Last Name" value={form.last_name || ""} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Walsh" />
          <Input label="Email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jordan@email.com" type="email" />
          <Input label="Phone" value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 555-0100" />
          <Select label="Role" value={form.role_id || ""} onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}>
            <option value="">Select a role...</option>
            {roles.filter(r => r.status === "open").map(r => { const cl = clients.find(c => c.id === r.client_id); return <option key={r.id} value={r.id}>{r.title} — {cl?.company_name}</option>; })}
          </Select>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={addCandidate}>Add Candidate</Btn>
          </div>
        </Modal>
      )}

      {modal === "client" && (
        <Modal title="Add Client" onClose={() => setModal(null)}>
          <Input label="Company Name" value={form.company_name || ""} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Acme Corp" />
          <Input label="Contact Name" value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" />
          <Input label="Contact Email" value={form.contact_email || ""} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="jane@acme.com" type="email" />
          <Input label="Industry" value={form.industry || ""} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} placeholder="Technology" />
          <Input label="Headcount" value={form.headcount || ""} onChange={e => setForm(f => ({ ...f, headcount: e.target.value }))} placeholder="100" type="number" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={addClient}>Add Client</Btn>
          </div>
        </Modal>
      )}

      {modal === "role" && (
        <Modal title={form.id ? "Edit Role" : "Add Role"} onClose={() => setModal(null)} width={560}>
          <Input label="Title" value={form.title || ""} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Senior Product Manager" />
          <Select label="Client" value={form.client_id || ""} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} disabled={!!form.id}>
            <option value="">Select a client...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </Select>
          <Input label="Department" value={form.department || ""} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="Engineering" />
          <Input label="Location" value={form.location || ""} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Remote" />
          <Input label="Calendly Label" value={form.calendly_label || ""} onChange={e => setForm(f => ({ ...f, calendly_label: e.target.value }))} placeholder="Must match Calendly dropdown exactly" />
          <Textarea label="Job Description" rows={5} value={form.job_description || ""} onChange={e => setForm(f => ({ ...f, job_description: e.target.value }))} placeholder="What this role does, scope of responsibility, team structure..." />
          <Textarea label="Key Requirements" rows={4} value={form.key_requirements || ""} onChange={e => setForm(f => ({ ...f, key_requirements: e.target.value }))} placeholder="Years of experience, must-have skills, domain expertise..." />
          <Textarea label="Screening Questions" rows={4} value={form.screening_questions || ""} onChange={e => setForm(f => ({ ...f, screening_questions: e.target.value }))} placeholder="Questions the AI should make sure to ask during screening..." />
          {form.id && form.status !== "closed" && <BenchMatchesPanel roleId={form.id} mode={mode} onOpenCandidate={(cid) => { setModal(null); nav("candidate", cid); }} />}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={saveRole}>{form.id ? "Save Changes" : "Add Role"}</Btn>
          </div>
        </Modal>
      )}

      {modal === "withdraw" && (
        <Modal title="Withdraw Candidate" onClose={() => setModal(null)}>
          <Select label="Reason" value={form.withdrawal_reason || ""} onChange={e => setForm(f => ({ ...f, withdrawal_reason: e.target.value }))}>
            <option value="">Select a reason...</option>
            <option value="Accepted another offer">Accepted another offer</option>
            <option value="Compensation">Compensation</option>
            <option value="Not interested">Not interested</option>
            <option value="Unresponsive / ghosted">Unresponsive / ghosted</option>
            <option value="Personal reasons">Personal reasons</option>
            <option value="Other">Other</option>
          </Select>
          <Textarea label="Notes (optional)" value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional context..." />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={confirmWithdraw} disabled={!form.withdrawal_reason}>Withdraw Candidate</Btn>
          </div>
        </Modal>
      )}

      {modal === "bench" && (
        <Modal title="Move to Bench" onClose={() => setModal(null)}>
          <Textarea label="Why keep them in mind?" rows={3} value={form.bench_reason || ""} onChange={e => setForm(f => ({ ...f, bench_reason: e.target.value }))} placeholder="e.g. strong technical depth, wrong location for this role" />
          <Input label="Follow up in (days)" type="number" min="1" value={form.follow_up_days ?? 45} onChange={e => setForm(f => ({ ...f, follow_up_days: e.target.value }))} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={confirmBench} disabled={!form.bench_reason}>Move to Bench</Btn>
          </div>
        </Modal>
      )}

      {modal === "closeRole" && (
        <Modal title="Close Role" onClose={() => setModal(null)}>
          <Select label="Reason" value={form.close_reason_type || ""} onChange={e => setForm(f => ({ ...f, close_reason_type: e.target.value }))}>
            <option value="">Select a reason...</option>
            <option value="Filled internally by client">Filled internally by client</option>
            <option value="Filled by us">Filled by us</option>
            <option value="Budget cut / role eliminated">Budget cut / role eliminated</option>
            <option value="Put on hold">Put on hold</option>
            <option value="Other">Other</option>
          </Select>
          <Textarea label="Notes (optional)" value={form.close_reason_notes || ""} onChange={e => setForm(f => ({ ...f, close_reason_notes: e.target.value }))} placeholder="e.g. CEC filled internally" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={closeRole} disabled={!form.close_reason_type}>Close Role</Btn>
          </div>
        </Modal>
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}